import React, { useEffect, useState } from "react";
import Notification from "../notification/Notification";
import DefaultButton from "../../styles/materialButtonStyle";
import MaterialDialog from "../../styles/materialDialog";
import { useTranslation } from "react-i18next";
import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../styles/styledDialog";
import {
  ATTRIBUTES, BOOLEAN_FLAGS,
  HTTP_STATUSES, MS_CASH,
  NULLABLE_ATTRIBUTES, PAYMENT_SYSTEM, PAYMENT_TYPE,
  SERVICES,
  STANDARD_ERRORS_WAY,
  TRANSACTION_TYPE_PAYMENT
} from "../../../utils/consts";
import ProjectsSelect from "../projects/ProjectsSelect";
import axios from "axios";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import { removeTransferFromCollection } from "../../../utils/collections";
import { parseAxiosError } from "../../../utils/response";
import ConnectionsSelect from "../connection/ConnectionsSelect";
import { DelayInput } from "react-delay-input/lib/Component";
import { TextField } from "@material-ui/core";
import ReservedBalanceAttributes from "./ReservedBalanceAttributes";
import { fetchInvoiceAttributes } from "../../../utils/fetchInvoiceAttributes";
import { v4 as uuidv4 } from "uuid";
import { validateCryptoWallet } from "../../../utils/attributesValidator";

const ReservedBalanceForm = ({ fetchTransactionList }) => {
  const { t } = useTranslation("paymentsPage");
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});

  const mandatoryDataForNextStep = {
    "service": null,
    "project": null,
    "paymentSystem": null,
    "currency": null,
    "amount": null,
  };

  const defaultData = Object.assign({}, mandatoryDataForNextStep, {
    "attributes": null,
    "connection": null,
    "currency": "USDT",
    "paymentSystem": "TRC20",
    "returnUrl": process.env.REACT_APP_DEFAULT_URL_FOR_INVOICE,
    "callBackUrl": process.env.REACT_APP_DEFAULT_URL_FOR_INVOICE,
    "transactionType": TRANSACTION_TYPE_PAYMENT.RESERVE,
  });

  const [invoiceData, setInvoiceData] = useState(defaultData);

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setInvoiceData(defaultData);
    setOpen(false);
    setErrors({});
  };

  const fetchServiceByProjectName = () => {
    axios.get("/api/connections/list-for-invoice/" + invoiceData.project.id, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK && "hydra:member" in response.data) {
        let service = [];

        response.data["hydra:member"].forEach(item => {
          service.push(item.service);
        });
        service = removeTransferFromCollection(service);

        const alpPayService = service.find(service => service.name === SERVICES.ALP_PAY);

        if (alpPayService) {
          setInvoiceData(prev => ({
            ...prev,
            service: alpPayService
          }));
        }
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const validateAttributes = (userAttribute, attributes) => {
    let checkFields = false;
    const attribute = attributes.find(attribute => attribute.id === userAttribute.attribute);
    const checkValue = userAttribute.value === undefined ? "" : userAttribute.value;

    let regexp = new RegExp(attribute.regex.replace(/^.|.$/g, ""));

    switch (attribute.name) {
      case ATTRIBUTES.WALLET: {
        if (!validateCryptoWallet(invoiceData.currency.asset, checkValue, invoiceData.paymentSystem.subName)) {
          setErrors((prevState) => ({
            ...prevState,
            [attribute.name]: `Invalid wallet, example - ${attribute.placeholder}`
          }));
          checkFields = true;
        }
      }
        break;
      case ATTRIBUTES.STABLING_CURRENCY: {
        let isStabling = attributes.find(attr => attr.name === ATTRIBUTES.IS_STABLING);

        if (isStabling.value === BOOLEAN_FLAGS.FALSE) {
          break;
        }

        if (checkValue === null || checkValue === "") {
          checkFields = true;
        }
      }
        break;
      default: {
        if (!regexp.test(checkValue)) {
          setErrors((prevState) => ({
            ...prevState,
            [attribute.name]: `Invalid ${attribute.title}, example - ${attribute.placeholder}`
          }));
          checkFields = true;
        }
      }
    }

    return checkFields;
  };

  const createInvoice = async (event) => {
    event.preventDefault();
    setDisabled(true);

    if (
      !invoiceData.paymentSystem ||
      !invoiceData.currency ||
      !invoiceData.amount ||
      !invoiceData.returnUrl ||
      !invoiceData.callBackUrl ||
      !invoiceData.connection?.id ||
      !invoiceData.transactionType
    ) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.checkFieldsMessage")
      });
      setDisabled(false);
      return;
    }

    let checkFields = false;

    let attributes = await fetchInvoiceAttributes(invoiceData.attributes);

    for (let [key, value] of Object.entries(attributes)) {

      if ((value.value === null || value.value === "") &&
        !NULLABLE_ATTRIBUTES.includes(invoiceData.attributes.find((element) => element.id === value.attribute)?.name)) {

        checkFields = true;
        break;
      }
    }

    for (let i = 0; i < attributes.length; i++) {
      const validationRes = validateAttributes(attributes[i], invoiceData.attributes);

      if (!checkFields) {
        checkFields = validationRes;
      }
    }

    if (checkFields) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.checkFieldsMessage")
      });
      setDisabled(false);
      return;
    }

    if (invoiceData.amount <= 0) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.invoiceDataMessage")
      });
      setDisabled(false);
      return;
    }

    let data = {
      "paymentSystem": invoiceData.paymentSystem,
      "amount": invoiceData.amount.replace(",", "."),
      "currency": invoiceData.currency,
      "attributes": attributes,
      "returnUrl": invoiceData.returnUrl,
      "callBackUrl": invoiceData.callBackUrl,
      "connection": invoiceData.connection.id,
      "paymentType": PAYMENT_TYPE.manual,
      "transactionType": invoiceData.transactionType,
      "referenceId": uuidv4(),
    };

    axios.post("/api/payments", data, userAuthenticationConfig(false)).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {

        if (response.data.status.value === "FAIL") {
          setNotification({
            ...notification,
            visible: true,
            type: "error",
            message: JSON.stringify(response.data.flowData)
          });
          return;
        }

        if (invoiceData.paymentSystem.subName !== PAYMENT_SYSTEM.BALANCE_REFILING) {
          setNotification({
            ...notification,
            visible: true,
            type: "error",
            message: t("create.errors.frameMessage")
          });
        }
        fetchTransactionList();
        setInvoiceData(defaultData);
        setOpen(false);
        setNotification({
          ...notification,
          visible: true,
          type: "success",
          message: t("create.notification.invoiceCreated")
        });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => setDisabled(false));
  };

  useEffect(() => {
    if (invoiceData.project && invoiceData.project.id) {
      fetchServiceByProjectName();
    }
  }, [invoiceData.project]);

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />

      <div className="reserved-balance-head__create-invoice">
        <DefaultButton
          color="success"
          onClick={handleClickOpen}
        >
          {t("create.create")}
        </DefaultButton>
      </div>

      <MaterialDialog
        open={open}
      >
        <StyledDialogWrapper>
          <StyledDialogHead>
            {t("create.title")}
          </StyledDialogHead>
          <StyledDialogContent>
            <ProjectsSelect
              checkedProjects={invoiceData.project}
              setCheckedProjects={(data) => setInvoiceData({ ...invoiceData, "project": data })}
              isObject={true}
            />
            {invoiceData.project?.id && invoiceData.service?.name &&
              <ConnectionsSelect
                checkedConnection={invoiceData.connection}
                setCheckedConnection={(data) => setInvoiceData({ ...invoiceData, "connection": data })}
                project={invoiceData.project.id}
                service={invoiceData.service.name}
                isObject={true}
                autoSelectSingle={true}
              />
            }
            {invoiceData.connection && (
              <>
                <DelayInput
                  id="amount"
                  delayTimeout={2000}
                  label={t("create.form.amount") + ", USDT (TRC20)"}
                  value={invoiceData.amount}
                  onChange={(data) => setInvoiceData({ ...invoiceData, "amount": data.target.value.replace(/ /g, "") })}
                  element={TextField}
                />
                <ReservedBalanceAttributes
                  invoiceData={invoiceData}
                  setInvoiceData={setInvoiceData}
                  setNotification={setNotification}
                  notification={notification}
                  errors={errors}
                  setErrors={setErrors}
                  handleClose={handleClose}
                />
              </>
            )}
          </StyledDialogContent>
          <StyledDialogAction col="2">
            <DefaultButton
              onClick={handleClose}
              color="danger"
              disabled={disabled}
            >
              {t("create.cancel")}
            </DefaultButton>
            <DefaultButton
              color="success"
              onClick={createInvoice}
              disabled={disabled || Object.keys(errors).length > 0}
            >
              {t("create.create")}
            </DefaultButton>
          </StyledDialogAction>
        </StyledDialogWrapper>
      </MaterialDialog>
    </>
  );
};

export default ReservedBalanceForm;